import React, { useEffect, useRef } from 'react';
import { useStore } from '../../store';
import { IntakeTabValidationKey } from '../../store/slices/intakeSlice';
import { useShallow } from 'zustand/react/shallow';
import { Controller } from 'react-hook-form';
import type {
  Control,
  UseFormRegister,
  UseFormSetValue,
  FieldErrors,
} from 'react-hook-form';
import {
  Alert,
  AlertDescription,
  Card,
  CardHeader,
  CardContent,
  Checkbox,
  FormSelect,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { AlertTriangle, Loader2, CalendarDays } from 'lucide-react';
import { DateField } from '../FormFields';
import { getDayAfterDateStr } from '../../utils/dateUtils';
import { useIntake } from '../../hooks/useIntake';
import { useSchedulingStepForm } from '../../hooks/useCreatePatientForm/useSchedulingStepForm';
import { useSchedulingClinicData } from '../../hooks/useCreatePatientForm/useSchedulingClinicData';
import {
  getMinEpisodeStartDateForMatchedPatient,
  getMinSocDateForMatchedPatient,
} from '../../hooks/useCreatePatientForm/useFormInitialization';
import { KinnserSchedulingStepFormContent } from '../../pages/CreatePatientForm/KinnserSchedulingStepFormContent';
import { RogerSchedulingStepFormContent } from '../../pages/CreatePatientForm/RogerSchedulingStepFormContent';
import { SchedulingStepEmptyState } from '../../pages/CreatePatientForm/SchedulingStepEmptyState';
import { FeatureFlag } from '../../constants/featureFlags';
import type { SchedulingStepFormData } from '../../schemas/schedulingStepFormSchema';
import MarkCompletedButton from './MarkCompletedButton';

interface SchedulingStepProps {
  onStatusChange?: () => void | Promise<void>;
}

type SchedulingEmr = 'kinnser' | 'roger';

/**
 * Build the patch applied when the user picks a visit type. Visit type drives
 * every other field: SOC pairs with "New admission" (or null if no discharge)
 * and clears the ROC date; ROC pairs with "Current episode".
 */
function getVisitTypePatch(
  emr: SchedulingEmr,
  v: 'soc' | 'roc',
  hasMatchedEpisodeDischargeDate: boolean
): Partial<SchedulingStepFormData> {
  const shared: Partial<SchedulingStepFormData> =
    v === 'soc'
      ? {
          kinnser_visit_type: 'soc',
          kinnser_creation_type: hasMatchedEpisodeDischargeDate
            ? 'new_admission'
            : null,
          roc_date: null,
        }
      : {
          kinnser_visit_type: 'roc',
          kinnser_creation_type: 'current_episode',
        };

  if (emr === 'kinnser') {
    return {
      ...shared,
      kinnser_create_episode: v === 'soc',
    };
  }
  return shared;
}

/**
 * Header checkbox for "Schedule in Roger/Kinnser". When `disabled` is set (match source
 * blocks this EMR), renders a cursor-not-allowed variant wrapped in a Tooltip explaining why.
 */
function ScheduleCheckbox({
  id,
  label,
  checked,
  disabled,
  disabledReason,
  onToggle,
}: {
  id: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  disabledReason?: string;
  onToggle: (checked: boolean) => void | Promise<void>;
}) {
  if (disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className='inline-flex items-center gap-2 cursor-not-allowed'>
            <Checkbox
              id={id}
              checked={checked}
              disabled
              onCheckedChange={() => {}}
            />
            <span className='text-sm font-medium text-gray-500'>{label}</span>
          </span>
        </TooltipTrigger>
        {disabledReason && (
          <TooltipContent side='bottom' className='max-w-xs text-xs'>
            {disabledReason}
          </TooltipContent>
        )}
      </Tooltip>
    );
  }
  return (
    <div className='flex items-center gap-2'>
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={val => onToggle(val === true)}
      />
      <label htmlFor={id} className='text-sm font-medium cursor-pointer'>
        {label}
      </label>
    </div>
  );
}

/**
 * Visit type / creation type / patient type row shown above the scheduling body for
 * an existing patient. The dropdown options and saved field names vary per EMR.
 */
function VisitCreationRow({
  emr,
  register,
  setValue,
  errors,
  visitType,
  hasMatchedEpisodeDischargeDate,
  handleFieldBlur,
  saveToServer,
}: {
  emr: SchedulingEmr;
  register: UseFormRegister<SchedulingStepFormData>;
  setValue: UseFormSetValue<SchedulingStepFormData>;
  errors: FieldErrors<SchedulingStepFormData>;
  visitType: 'soc' | 'roc' | null | undefined;
  hasMatchedEpisodeDischargeDate: boolean;
  handleFieldBlur: (e: React.FocusEvent<HTMLElement>) => void;
  saveToServer: (data: Partial<SchedulingStepFormData>) => Promise<void> | void;
}) {
  // Visit type / creation type are stored on shared keys across EMRs.
  const visitField = 'kinnser_visit_type' as const;
  const creationField = 'kinnser_creation_type' as const;

  const creationOptions =
    visitType === 'soc'
      ? [
          {
            value: 'new_admission' as const,
            label: 'New admission',
            disabled: !hasMatchedEpisodeDischargeDate,
            disabledReason:
              'Cannot select because patient has not been discharged.',
          },
        ]
      : visitType === 'roc'
        ? [{ value: 'current_episode' as const, label: 'Current episode' }]
        : [];

  return (
    <div className='mb-4 pb-4 border-b border-gray-200 flex items-center gap-6 flex-wrap'>
      <div className='flex items-center gap-2'>
        <FormSelect
          label='Visit type'
          placeholder='Select visit type...'
          options={[
            { value: 'soc', label: 'SOC' },
            { value: 'roc', label: 'ROC' },
          ]}
          error={errors[visitField]?.message}
          {...register(visitField, {
            onBlur: handleFieldBlur,
            onChange: async e => {
              const v = e.target.value;
              if (v !== 'soc' && v !== 'roc') return;
              const patch = getVisitTypePatch(
                emr,
                v,
                hasMatchedEpisodeDischargeDate
              );
              (
                Object.keys(patch) as Array<keyof SchedulingStepFormData>
              ).forEach(key => {
                setValue(
                  key,
                  patch[
                    key
                  ] as SchedulingStepFormData[keyof SchedulingStepFormData],
                  { shouldDirty: true }
                );
              });
              await saveToServer(patch);
            },
          })}
        />
      </div>
      <div className='flex items-center gap-2'>
        <FormSelect
          label='Creation type'
          placeholder='Select creation type...'
          options={creationOptions}
          error={errors[creationField]?.message}
          {...register(creationField, {
            onBlur: handleFieldBlur,
            onChange: async e => {
              let v:
                | 'new_admission'
                | 'new_episode'
                | 'current_episode'
                | null =
                e.target.value === 'new_admission' ||
                e.target.value === 'new_episode' ||
                e.target.value === 'current_episode'
                  ? e.target.value
                  : null;
              if (v === 'new_admission' && !hasMatchedEpisodeDischargeDate) {
                v = null;
              }
              setValue(creationField, v, { shouldDirty: true });
              if (v) await saveToServer({ [creationField]: v });
            },
          })}
        />
      </div>
      <div className='flex flex-col gap-1.5'>
        <label className='text-sm font-medium text-gray-700'>
          Patient type
        </label>
        <Input
          value='Existing'
          disabled
          readOnly
          inputSize='default'
          className='w-32 bg-gray-50'
        />
      </div>
    </div>
  );
}

/**
 * ROC date picker rendered inline under the visit/creation row for either EMR.
 * Clamps values below `minRocDateStr` up to that minimum.
 */
function RocDateBlock({
  htmlFor,
  control,
  errors,
  minRocDateStr,
  minRocDate,
  saveToServer,
}: {
  htmlFor: string;
  control: Control<SchedulingStepFormData>;
  errors: FieldErrors<SchedulingStepFormData>;
  minRocDateStr: string | null;
  minRocDate: Date | undefined;
  saveToServer: (data: Partial<SchedulingStepFormData>) => Promise<void> | void;
}) {
  return (
    <div className='flex flex-col gap-6'>
      <div className='w-fit'>
        <Controller
          name='roc_date'
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <DateField
              label='ROC date'
              value={value}
              htmlFor={htmlFor}
              required={true}
              disabled={false}
              error={errors.roc_date?.message}
              onChange={val => {
                const norm =
                  val && typeof val === 'string' ? val.split('T')[0] : null;
                const effective =
                  norm && minRocDateStr && norm < minRocDateStr
                    ? minRocDateStr
                    : val;
                onChange(effective);
                if (effective) {
                  void saveToServer({ roc_date: effective });
                }
              }}
              onBlur={onBlur}
              minDate={minRocDate}
              yearRange={{
                fromYear: new Date().getFullYear() - 1,
                toYear: new Date().getFullYear() + 1,
              }}
            />
          )}
        />
      </div>
      <div className='border-b border-gray-200' />
    </div>
  );
}

/** Shown alongside scheduling forms when an existing patient still needs visit + creation type. */
function SchedulingExistingPatientGuide() {
  return (
    <div
      role='note'
      className='flex flex-col items-center py-6 px-4 text-center shrink-0'
    >
      <div className='mb-3'>
        <CalendarDays className='h-7 w-7 text-gray-500' />
      </div>
      <h3 className='text-base font-medium text-gray-900'>
        Select visit type and creation type
      </h3>
      <p className='text-sm text-gray-600 mt-1 max-w-md'>
        Use the visit type and creation type fields above to finish setup before
        scheduling.
      </p>
    </div>
  );
}

/**
 * Inner content that holds the form. Keyed by clinic data availability so the form
 * remounts when clinicData loads and gets a resolver that includes clinic-conditional
 * rules (e.g. secondary scheduler required). Otherwise validation errors don't show
 * until the user switches tabs and comes back.
 */
const SchedulingStepContent: React.FC<SchedulingStepProps> = ({
  onStatusChange,
}) => {
  const { hasFeatureFlag, user } = useStore(
    useShallow(state => ({
      hasFeatureFlag: state.hasFeatureFlag,
      user: state.user,
    }))
  );

  const {
    // Form
    control,
    register,
    errors,
    setValue,
    getValues,
    handleFieldBlur,

    // Watched values
    pushToKinnser,
    rogerCreateEpisode,
    visitType,
    creationType,

    // Derived intake/matching state
    isExistingPatient,
    hasMatchedEpisodeDischargeDate,
    patientMatchSource,
    minRocDateStr,
    minRocDate,

    // Save status
    saveStatus,
    saveToServer,

    // Clinics (Kinnser path)
    clinicData,
    loadingClinicData,

    // Roger UserClinic-based data
    rogerClinicianOptions,
    rogerCaseManagerOptions,
    loadingRogerClinicians,

    // Intake data
    selectedIntake,

    // Form validation (synced to store so Push to EMR can block onboarding when invalid)
    isValid,
  } = useSchedulingStepForm();

  const setIntakeTabValid = useStore(state => state.setIntakeTabValid);

  if (!selectedIntake) {
    return (
      <div className='h-screen flex items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin text-gray-400' />
      </div>
    );
  }

  const isNewPatient = (selectedIntake as { is_new_patient?: boolean | null })
    .is_new_patient;
  const isNewPatientSet = isNewPatient === true || isNewPatient === false;
  const allowSelectExistingPatient = hasFeatureFlag(
    FeatureFlag.INTAKE_EXISTING_PATIENT_WORKFLOW
  );
  /** Existing patient matched only in Roger → cannot schedule in Kinnser. */
  const scheduleKinnserBlockedByMatch =
    isExistingPatient && patientMatchSource === 'roger_emr';
  /** Existing patient matched only in Kinnser → cannot schedule in Roger. */
  const scheduleRogerBlockedByMatch =
    isExistingPatient && patientMatchSource === 'kinnser';
  /** Extraction has not confirmed new vs existing but user chose Kinnser and/or Roger scheduling. */
  const schedulingNeedsPatientConfirmation =
    allowSelectExistingPatient &&
    !isNewPatientSet &&
    (pushToKinnser || rogerCreateEpisode);

  // For existing patients, minimum episode start date is after prior episode's start date (and SOC/discharge).
  const minEpisodeStartDate = isExistingPatient
    ? getMinEpisodeStartDateForMatchedPatient(
        selectedIntake as unknown as Parameters<
          typeof getMinEpisodeStartDateForMatchedPatient
        >[0]
      )
    : null;

  // For existing patients with SOC visit type, minimum SOC date is after prior discharge/start of care.
  const minSocDateStr = isExistingPatient
    ? getMinSocDateForMatchedPatient(
        selectedIntake as unknown as Parameters<
          typeof getMinSocDateForMatchedPatient
        >[0]
      )
    : null;

  const matchedStartOfCareRaw = (
    selectedIntake as { kinnser_matched_start_of_care?: string | null }
  )?.kinnser_matched_start_of_care;
  const matchedStartOfCareStr =
    matchedStartOfCareRaw && typeof matchedStartOfCareRaw === 'string'
      ? matchedStartOfCareRaw.split('T')[0]
      : null;
  const matchedEpisodeEndRaw = (
    selectedIntake as { kinnser_matched_episode_end_date?: string | null }
  )?.kinnser_matched_episode_end_date;
  const matchedEpisodeEndStr =
    matchedEpisodeEndRaw && typeof matchedEpisodeEndRaw === 'string'
      ? matchedEpisodeEndRaw.split('T')[0]
      : null;
  const lockedEpisodeStartDateStr = getDayAfterDateStr(
    matchedEpisodeEndStr ?? undefined
  );
  /** SOC + new admission: episode start uses SOC min, not day-after prior episode end. */
  const minEpisodeStartForNewAdmission =
    creationType === 'new_admission'
      ? (minSocDateStr ?? minEpisodeStartDate)
      : minEpisodeStartDate;

  let tabIsValid: boolean;
  if (schedulingNeedsPatientConfirmation) {
    tabIsValid = false;
  } else if (isExistingPatient) {
    // Existing patients: scheduling step does not block on the same RHF rules as new admits.
    tabIsValid = true;
  } else {
    tabIsValid = isValid;
  }
  const lastValidRef = useRef(tabIsValid);
  lastValidRef.current = tabIsValid;

  // Report this tab's validation state so Push to EMR can block onboarding when any tab has errors.
  // On unmount (e.g. switching to Push to EMR), cleanup writes last-known validity so the store stays correct.
  useEffect(() => {
    setIntakeTabValid(IntakeTabValidationKey.Scheduling, tabIsValid);
    return () => {
      setIntakeTabValid(
        IntakeTabValidationKey.Scheduling,
        lastValidRef.current
      );
    };
  }, [setIntakeTabValid, tabIsValid]);

  // Body routing. For each EMR: ROC + creation → inline ROC date; SOC + creation (or
  // new patient) → full SOC form. Existing patient with no visit/creation chosen
  // shows the guide below whichever body (or empty state) is rendered.
  const showKinnserScheduling = pushToKinnser;
  const showRogerScheduling = rogerCreateEpisode && !pushToKinnser;

  const kinnserSocFormReady =
    showKinnserScheduling &&
    (isNewPatient === true ||
      (!allowSelectExistingPatient && !isNewPatientSet) ||
      (isExistingPatient && visitType === 'soc' && Boolean(creationType)));

  const kinnserRocReady =
    showKinnserScheduling && visitType === 'roc' && Boolean(creationType);

  const rogerSocFormReady =
    showRogerScheduling &&
    (!isExistingPatient || (visitType === 'soc' && Boolean(creationType)));

  const rogerRocReady =
    showRogerScheduling && visitType === 'roc' && Boolean(creationType);

  const showExistingPatientGuide =
    isExistingPatient &&
    (showKinnserScheduling || showRogerScheduling) &&
    (!visitType || !creationType);

  return (
    <Card className='h-full flex flex-col'>
      <CardHeader className='border-b py-3 px-4'>
        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center gap-3'>
            {saveStatus === 'saving' && (
              <div className='flex items-center gap-2 text-xs text-gray-500'>
                <Loader2 className='h-3 w-3 animate-spin' />
                <span>Saving...</span>
              </div>
            )}
            {saveStatus === 'saved' && (
              <div className='text-xs text-green-600'>Saved</div>
            )}
            {saveStatus === 'error' && (
              <div className='text-xs text-red-600'>Error saving</div>
            )}
          </div>
          {selectedIntake && (
            <MarkCompletedButton
              intakeId={selectedIntake.id}
              tabKey='scheduling'
              isComplete={
                selectedIntake.tab_statuses?.scheduling === 'complete' ||
                selectedIntake.tab_statuses?.scheduling ===
                  'complete_with_errors'
              }
              onStatusChange={onStatusChange}
              className='ml-2'
            />
          )}
        </div>
        <TooltipProvider delayDuration={300}>
          <div className='flex items-center gap-6'>
            {user?.agency?.is_rogeremr_enabled && (
              <Controller
                name='roger_create_episode'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <ScheduleCheckbox
                    id='createRogerEpisodeEnabled'
                    label='Schedule in Roger'
                    checked={!!value}
                    disabled={scheduleRogerBlockedByMatch}
                    disabledReason='This patient was matched in Kinnser. An existing Kinnser patient cannot be scheduled in Roger.'
                    onToggle={async isChecked => {
                      onChange(isChecked);
                      if (isChecked) {
                        setValue('kinnser_create_episode', false);
                        setValue('roger_create_episode', true);
                        setValue('push_to_roger', true);
                        setValue('push_to_kinnser', false);
                        await saveToServer({
                          roger_create_episode: true,
                          kinnser_create_episode: false,
                          push_to_roger: true,
                          push_to_kinnser: false,
                        });
                      } else {
                        setValue('push_to_roger', false);
                        await saveToServer({
                          roger_create_episode: false,
                          push_to_roger: false,
                        });
                      }
                    }}
                  />
                )}
              />
            )}
            {user?.agency?.is_kinnser_enabled && (
              <Controller
                name='push_to_kinnser'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <ScheduleCheckbox
                    id='scheduleInKinnser'
                    label='Schedule in Kinnser'
                    checked={!!value}
                    disabled={scheduleKinnserBlockedByMatch}
                    disabledReason='This patient was matched in Roger. An existing Roger patient cannot be scheduled in Kinnser.'
                    onToggle={async isChecked => {
                      onChange(isChecked);
                      if (isChecked) {
                        setValue('roger_create_episode', false);
                        setValue('push_to_roger', false);
                        setValue('push_to_kinnser', true);
                        if (isNewPatient) {
                          setValue('kinnser_create_episode', true);
                        }
                        await saveToServer({
                          push_to_kinnser: true,
                          roger_create_episode: false,
                          push_to_roger: false,
                          ...(isNewPatient
                            ? { kinnser_create_episode: true }
                            : {}),
                        });
                      } else {
                        setValue('push_to_kinnser', false);
                        setValue('kinnser_create_episode', false);
                        await saveToServer({
                          push_to_kinnser: false,
                          kinnser_create_episode: false,
                        });
                      }
                    }}
                  />
                )}
              />
            )}
          </div>
        </TooltipProvider>
      </CardHeader>
      <CardContent className='flex-1 overflow-y-auto p-4 pt-3'>
        {schedulingNeedsPatientConfirmation ? (
          <div className='flex flex-col items-center h-full py-12 px-4 text-center'>
            <div className='bg-amber-50 p-4 rounded-full mb-4'>
              <AlertTriangle className='h-8 w-8 text-amber-600' />
            </div>
            <h3 className='text-lg font-medium text-gray-900'>
              Patient must be confirmed before scheduling
            </h3>
            <p className='text-sm text-gray-500 mt-1 max-w-sm'>
              The patient must be confirmed as a new or existing patient on the
              Extraction tab before scheduling.
            </p>
          </div>
        ) : (
          <div className='flex flex-col gap-4 min-h-0'>
            {isExistingPatient && showKinnserScheduling && (
              <VisitCreationRow
                emr='kinnser'
                register={register}
                setValue={setValue}
                errors={errors}
                visitType={visitType}
                hasMatchedEpisodeDischargeDate={hasMatchedEpisodeDischargeDate}
                handleFieldBlur={handleFieldBlur}
                saveToServer={saveToServer}
              />
            )}
            {isExistingPatient && showRogerScheduling && (
              <VisitCreationRow
                emr='roger'
                register={register}
                setValue={setValue}
                errors={errors}
                visitType={visitType}
                hasMatchedEpisodeDischargeDate={hasMatchedEpisodeDischargeDate}
                handleFieldBlur={handleFieldBlur}
                saveToServer={saveToServer}
              />
            )}

            {kinnserRocReady || rogerRocReady ? (
              <RocDateBlock
                htmlFor={kinnserRocReady ? 'rocDate' : 'rogerRocDate'}
                control={control}
                errors={errors}
                minRocDateStr={minRocDateStr}
                minRocDate={minRocDate}
                saveToServer={saveToServer}
              />
            ) : kinnserSocFormReady ? (
              <KinnserSchedulingStepFormContent
                control={control}
                register={register}
                errors={errors}
                setValue={setValue}
                getValues={getValues}
                handleFieldBlur={handleFieldBlur}
                saveToServer={saveToServer}
                clinicData={clinicData}
                loadingClinicData={loadingClinicData}
                createResult={null}
                isExistingPatient={isExistingPatient}
                selectedIntake={selectedIntake}
                kinnserCreationType={creationType ?? 'new_episode'}
                minEpisodeStartDate={
                  isExistingPatient ? minEpisodeStartForNewAdmission : null
                }
                minSocDate={isExistingPatient ? minSocDateStr : null}
                socDateLockedToMatchedEpisode={
                  isExistingPatient &&
                  !hasMatchedEpisodeDischargeDate &&
                  creationType === 'new_episode' &&
                  !!matchedStartOfCareStr
                }
                lockedSocDateStr={
                  isExistingPatient ? matchedStartOfCareStr : null
                }
                episodeStartLockedToMatchedEnd={
                  isExistingPatient &&
                  creationType === 'new_episode' &&
                  !!lockedEpisodeStartDateStr
                }
                lockedEpisodeStartDateStr={
                  isExistingPatient ? lockedEpisodeStartDateStr : null
                }
                episodeStartLockedToSoc={
                  isExistingPatient && creationType === 'new_admission'
                }
              />
            ) : rogerSocFormReady ? (
              <RogerSchedulingStepFormContent
                control={control}
                register={register}
                setValue={setValue}
                errors={errors}
                handleFieldBlur={handleFieldBlur}
                saveToServer={saveToServer}
                rogerClinicianOptions={rogerClinicianOptions}
                rogerCaseManagerOptions={rogerCaseManagerOptions}
                loadingRogerClinicians={loadingRogerClinicians}
                isExistingPatient={isExistingPatient}
              />
            ) : showExistingPatientGuide ? (
              <SchedulingExistingPatientGuide />
            ) : (
              <SchedulingStepEmptyState />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SchedulingStep: React.FC<SchedulingStepProps> = ({ onStatusChange }) => {
  const { hasFeatureFlag } = useStore(
    useShallow(state => ({
      hasFeatureFlag: state.hasFeatureFlag,
    }))
  );
  const { selectedIntake } = useIntake();
  const user = useStore(state => state.user);
  const setIntakeTabValid = useStore(state => state.setIntakeTabValid);
  const { clinicData, loadingClinicData } = useSchedulingClinicData({
    agencySystemName: user?.agency?.system_name,
    kinnserClinicId: selectedIntake?.kinnser_clinic_id,
  });

  // While clinic data is loading the form is not mounted; don't leave Scheduling as "valid" from on-load.
  useEffect(() => {
    if (loadingClinicData) {
      setIntakeTabValid(IntakeTabValidationKey.Scheduling, null);
    }
  }, [loadingClinicData, setIntakeTabValid]);

  // Check if user has permission to create patients
  if (!hasFeatureFlag(FeatureFlag.INTAKE_SCHEDULING_SUPPORT)) {
    return (
      <div className='p-6'>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex gap-2 items-center'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don't have permission to create patients. Please contact your
            administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!selectedIntake) {
    return (
      <div className='h-screen flex items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin text-gray-400' />
      </div>
    );
  }

  // Show loading spinner for entire section while clinic info is loading
  if (loadingClinicData) {
    return (
      <div className='h-full flex flex-col'>
        <div className='h-full flex items-center justify-center'>
          <div className='flex flex-col items-center gap-4'>
            <Loader2 className='h-8 w-8 animate-spin text-gray-400' />
          </div>
        </div>
      </div>
    );
  }

  // Key by clinic data so the form remounts when clinicData loads and gets the correct
  // resolver (e.g. secondary scheduler required). Ensures validation errors show immediately.
  const formKey = clinicData != null ? 'loaded' : 'loading';
  return (
    <SchedulingStepContent key={formKey} onStatusChange={onStatusChange} />
  );
};

export default SchedulingStep;
